import { Button, IconButton, Input, Modal } from '@hue/design-system';
import { Undo2 } from 'lucide-react';
import { useState } from 'react';
import { getApiError, materialUsageApi, type MaterialUsageLine } from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { RequestAlert } from '../../app/data-view.js';

import { unitLabels } from './units.js';

/// As linhas já registradas de um atendimento, com o estorno. Aparecem na conferência e no
/// fechamento: corrigir é estornar e lançar de novo, nos dois lugares (ADR 0029, decisão 15).
export function RegisteredLines({
  lines,
  canReverse,
  timeZone,
  onReverse,
}: {
  lines: readonly MaterialUsageLine[];
  canReverse: boolean;
  timeZone: string;
  onReverse: (line: MaterialUsageLine) => void;
}) {
  if (lines.length === 0) return null;
  return (
    <div className="grid gap-2">
      <h2 className="text-sm font-semibold">Linhas registradas</h2>
      <ul className="grid gap-2">
        {lines.map((line) => (
          <li
            key={line.id}
            className="flex items-start justify-between gap-3 rounded-2xl border border-surface-border p-3 text-sm"
          >
            <div className="min-w-0">
              <strong className={line.reversedAt ? 'line-through' : undefined}>
                {line.productDisplayName} · {line.quantity} {unitLabels[line.baseUnit]}
              </strong>
              <span className="mt-1 block text-xs text-content-muted">
                {line.locationDisplayName}
                {line.lotCode ? ` · lote ${line.lotCode}` : ''} ·{' '}
                {formatDateTime(line.occurredAt, timeZone)} · {line.registeredByDisplayName}
              </span>
              {line.reversedAt ? (
                <span className="mt-1 block text-xs text-content-faint">
                  Estornado: {line.reversalReason}
                </span>
              ) : null}
            </div>
            {canReverse && !line.reversedAt ? (
              <IconButton
                label="Estornar consumo"
                tone="danger"
                icon={<Undo2 className="size-4" />}
                onClick={() => onReverse(line)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReverseDialog({
  line,
  onOpenChange,
  onReversed,
}: {
  line: MaterialUsageLine;
  onOpenChange: (open: boolean) => void;
  onReversed: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [requestError, setRequestError] = useState<string>();

  async function submit() {
    setRequestError(undefined);
    try {
      await materialUsageApi.reverse(line.id, reason);
      await onReversed();
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Estornar consumo"
      description={`O movimento de ${line.quantity} ${unitLabels[line.baseUnit]} de ${line.productDisplayName} volta ao estoque. A linha errada fica registrada, e a certa é lançada em seguida.`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Manter
          </Button>
          <Button disabled={reason.trim().length < 3} onClick={() => void submit()}>
            Estornar
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Input
          label="Motivo"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Por que esse consumo foi desfeito?"
        />
        {requestError ? <RequestAlert message={requestError} /> : null}
      </div>
    </Modal>
  );
}
